// TutorPanel.js — el tutor del modo Mentor, como panel al lado del video.
//
// Antes el chat vivía en una barra de 44px al fondo de la pantalla, debajo de la
// navegación: en un portátil de 768px de alto quedaba fuera de vista y nadie
// descubría que tenía un tutor que se sabe la lección. Ahora es una columna con
// identidad propia —la cara del docente del curso— y con preguntas ya escritas,
// que es lo que hace que el alumno se anime a preguntar.
//
// El panel no tiene estado propio de conversación: MentorPage sigue siendo el
// dueño de los mensajes, el quiz y la voz. Aquí solo se presenta.
import React, { useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

const ANCHO = '360px';

// Un solo momento de movimiento: la llegada del panel. Nada de entradas
// encadenadas ni hover animado en cada elemento.
const entrar = keyframes`
  from { opacity: 0; transform: translateX(14px); }
  to { opacity: 1; transform: translateX(0); }
`;

const latido = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(8,145,178,0.5); }
  50% { box-shadow: 0 0 0 9px rgba(8,145,178,0); }
`;

// ─── Contenedor ───
const Dock = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100vh;
  background: var(--m-bg-sidebar);
  /* Solo la línea que da al video; en el borde de la pantalla no pinta nada. */
  ${p => p.$alaIzquierda
    ? 'border-right: 1px solid var(--m-border);'
    : 'border-left: 1px solid var(--m-border);'}
  /* El índice de lecciones y el tutor nunca comparten lado: el tutor siempre
     queda pegado al video. */
  order: ${p => (p.$alaIzquierda ? 1 : 3)};
  animation: ${entrar} 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  /* Tablet: se superpone al contenido en vez de robarle ancho al video. */
  @media (max-width: 1279px) {
    position: fixed;
    top: 0;
    right: 0;
    width: min(${ANCHO}, 92vw);
    z-index: 1100;
    box-shadow: -8px 0 32px rgba(0,0,0,0.35);
  }

  /* Móvil: hoja inferior, el video sigue a la vista arriba. */
  @media (max-width: 768px) {
    top: auto;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 72vh;
    height: 72dvh;   /* la barra del navegador móvil cambia de alto al hacer scroll */
    border-radius: 16px 16px 0 0;
    border-left: none;
    border-right: none;
    border-top: 1px solid var(--m-border);
    box-shadow: 0 -8px 32px rgba(0,0,0,0.35);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Velo = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1099;
  }
`;

// ─── Cabecera: el docente, no un robot ───
const Cabecera = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px 14px 16px;
  border-bottom: 1px solid var(--m-border);
  flex-shrink: 0;
`;

const Avatar = styled.span`
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: visible;
  ${p => p.$destacar && css`animation: ${latido} 1.6s ease-out 3;`}

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const AvatarFoto = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--m-border-light);
`;

const AvatarInicial = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(140deg, #0891B2, #0e7490);
`;

const Bandera = styled.img`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 16px;
  height: 11px;
  border-radius: 2px;
  box-shadow: 0 0 0 1.5px var(--m-bg-sidebar);
`;

const Identidad = styled.div`
  min-width: 0;
  flex: 1;
`;

const Nombre = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--m-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Papel = styled.div`
  font-size: 0.75rem;
  color: var(--m-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CerrarBtn = styled.button`
  background: none;
  border: none;
  color: var(--m-text-muted);
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:hover { color: var(--m-text-secondary); background: var(--m-bg-hover); }
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: 2px; }

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

// ─── Conversación ───
const Conversacion = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--m-scrollbar-thumb); border-radius: 2px; }
`;

const Saludo = styled.div`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--m-text-secondary);

  strong {
    display: block;
    color: var(--m-text-primary);
    font-size: 1.02rem;
    font-weight: 600;
    margin-bottom: 4px;
  }
`;

// El tutor habla en texto corrido, como una persona; el alumno queda en burbuja.
// Dos formas distintas se distinguen mejor que dos tarjetas iguales de colores
// distintos, y el texto corrido deja más ancho para leer.
const Respuesta = styled.div`
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--m-text-secondary);
  white-space: pre-wrap;
  ${p => p.$atenuado && 'opacity: 0.6;'}
  ${p => p.$error && 'color: #f87171;'}
  ${p => p.$aviso && css`
    color: #fbbf24;
    background: rgba(251,191,36,0.1);
    border: 1px solid rgba(251,191,36,0.35);
    border-radius: 10px;
    padding: 10px 12px;
  `}
`;

const Pregunta = styled.div`
  align-self: flex-end;
  max-width: 85%;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 14px 14px 4px 14px;
  background: var(--m-chat-user-bg);
  color: var(--m-chat-user-text);
  white-space: pre-wrap;
`;

// ─── Sugerencias ───
const Sugerencias = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 16px 10px;
  flex-shrink: 0;
`;

const SugerenciaBtn = styled.button`
  text-align: left;
  font-size: 0.8rem;
  line-height: 1.35;
  color: var(--m-text-secondary);
  background: var(--m-bg-card);
  border: 1px solid var(--m-border-medium);
  border-radius: 10px;
  padding: 8px 11px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(8,145,178,0.5); color: var(--m-text-primary); }
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  @media (max-width: 768px) {
    padding: 11px 13px;
    font-size: 0.85rem;
  }
`;

const Aviso = styled.div`
  margin: 0 16px 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(8,145,178,0.35);
  background: rgba(8,145,178,0.08);
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--m-text-secondary);
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
  }
`;

const AvisoAccion = styled.button`
  color: #22d3ee !important;
  font-weight: 600;
  text-align: left;
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: 2px; }
`;

const AvisoCerrar = styled.button`
  margin-left: auto;
  color: var(--m-text-muted) !important;
  font-size: 1rem;
  line-height: 1;
  flex-shrink: 0;
`;

// ─── Escribir ───
const Escribir = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--m-border);
  flex-shrink: 0;

  /* En móvil, debajo del campo está la franja del gesto de inicio del iPhone:
     sin esto el botón de enviar queda justo encima y se toca mal. */
  @media (max-width: 768px) {
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  }
`;

const Campo = styled.input`
  flex: 1;
  min-width: 0;
  background: var(--m-bg-input);
  border: 1px solid var(--m-border-medium);
  border-radius: 20px;
  padding: 10px 14px;
  color: var(--m-text-primary);
  font-size: 0.875rem;
  outline: none;
  transition: background 0.2s, border-color 0.2s;
  &:focus { border-color: rgba(8,145,178,0.5); background: var(--m-bg-input-focus); }
  &::placeholder { color: var(--m-text-muted); }

  /* Por debajo de 16px, iOS hace zoom al enfocar el campo y descuadra todo. */
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 11px 14px;
  }
`;

const Redondo = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: filter 0.15s;
  border: 1px solid var(--m-border-medium);
  background: var(--m-bg-input);
  color: var(--m-text-secondary);

  ${p => p.$primario && css`
    border: none;
    background: linear-gradient(135deg, #0891B2, #0e7490);
    color: #fff;
  `}
  ${p => p.$grabando && css`
    background: rgba(239,68,68,0.9);
    border-color: rgba(239,68,68,0.5);
    color: #fff;
  `}
  &:hover:not(:disabled) { filter: brightness(1.12); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: 2px; }
`;

const EstadoVoz = styled.div`
  padding: 0 16px 8px;
  font-size: 0.78rem;
  color: #22d3ee;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

// ─── Invitación a preguntar (panel cerrado) ───
// En escritorio es una pastilla flotante abajo a la derecha. En móvil NO puede
// flotar: caía justo encima del botón «Siguiente», que es la acción más
// importante de la pantalla. Ahí baja al flujo, debajo de la navegación, como
// una barra de ancho completo que no puede taparle nada a nadie.
const Llamador = styled.button`
  position: fixed;
  right: 18px;
  /* Por encima de la barra Anterior / Siguiente, que también está abajo. */
  /* Con contenido embebido hay además la barra «Marcar como completada». */
  bottom: ${p => (p.$alto ? 150 : 84)}px;
  bottom: calc(${p => (p.$alto ? 150 : 84)}px + env(safe-area-inset-bottom, 0px));
  z-index: 1098;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 8px;
  border-radius: 999px;
  border: 1px solid var(--m-border-light);
  background: var(--m-bg-sidebar);
  color: var(--m-text-primary);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 28px rgba(0,0,0,0.35);
  transition: filter 0.15s;
  &:hover { filter: brightness(1.1); }
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: 2px; }

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    justify-content: flex-start;
    border-radius: 12px;
    padding: 10px 14px 10px 10px;
    margin: 0 0 4px;
    box-shadow: none;
    background: var(--m-bg-card);
  }

  /* Celular acostado: no hay alto para una pastilla sobre el video; queda
     solo la cara del tutor, arriba a la derecha junto al título. */
  @media (max-height: 500px) and (orientation: landscape) {
    top: 4px;
    bottom: auto;
    right: 10px;
    padding: 3px;
    gap: 0;
    & > :not(:first-child) { display: none; }
  }
`;

// La foto del docente sola parece un avatar, no un botón. El globo de diálogo
// es lo que dice "esto se toca para preguntar".
const IconoPreguntar = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: #22d3ee;
  flex-shrink: 0;
`;

const TextoLlamador = styled.span`
  text-align: left;
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Punto = styled.span`
  position: absolute;
  top: 6px;
  right: 12px;
  @media (max-width: 768px) { top: 8px; right: 8px; }
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 2px var(--m-bg-sidebar);
`;

// ─── Presentación del quiz ───
const Pasos = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 5px;
`;

const Paso = styled.span`
  width: 18px;
  height: 3px;
  border-radius: 2px;
  background: ${p => (p.$activo ? '#F59E0B' : 'var(--m-border-light)')};
`;

const IconoEnviar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const IconoMic = ({ activo }) => (activo ? (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
  </svg>
));

/** Foto del docente o, si no hay, su inicial. */
const CaraDocente = ({ docente, destacar }) => (
  <Avatar $destacar={destacar}>
    {docente?.foto
      ? <AvatarFoto src={docente.foto} alt="" loading="lazy" />
      : <AvatarInicial>{(docente?.nombre || '?').charAt(0).toUpperCase()}</AvatarInicial>}
    {docente?.bandera && <Bandera src={docente.bandera} alt="" loading="lazy" />}
  </Avatar>
);


/**
 * La invitación a preguntar cuando el panel está cerrado.
 *
 * Va montada dentro del área del video a propósito: en móvil tiene que quedar
 * DEBAJO de la barra de navegación, en el flujo, porque flotando se ponía justo
 * encima del botón «Siguiente». En escritorio el CSS la saca del flujo y la deja
 * flotando abajo a la derecha.
 */
export function TutorLlamador({ docente, aviso, destacar = false, alto = false, onAbrir, t = (k) => k }) {
  const invitacion = docente?.nombre
    ? t('tutor_preguntar_a').replace('{nombre}', docente.nombre)
    : t('tutor_preguntar_tutor');
  return (
    <Llamador type="button" $alto={alto} onClick={onAbrir} title={invitacion} aria-label={invitacion}>
      <CaraDocente docente={docente} destacar={destacar} />
      <TextoLlamador>{invitacion}</TextoLlamador>
      <IconoPreguntar>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" />
        </svg>
      </IconoPreguntar>
      {(aviso || destacar) && <Punto />}
    </Llamador>
  );
}

export default function TutorPanel({
  abierto,
  onAbrir,
  onCerrar,
  docente,
  userName,
  mensajes = [],
  cargando = false,
  parcial = '',
  error = '',
  micNoDisponible = false,
  input,
  onInput,
  onSubmit,
  quizActive = false,
  quizPregunta = 1,
  sugerencias = [],
  onSugerencia,
  voz = {},
  aviso = null,
  onAviso,
  onDescartarAviso,
  destacar = false,
  indiceALaDerecha = false,
  t = (k) => k,
}) {
  const finRef = useRef(null);
  const campoRef = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    finRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [abierto, mensajes, parcial, cargando]);

  // Al abrirlo, el cursor ya está donde se escribe (salvo en móvil, donde eso
  // levantaría el teclado encima del video).
  useEffect(() => {
    if (abierto && window.innerWidth > 768) campoRef.current?.focus();
  }, [abierto]);

  const nombreTutor = docente?.nombre || t('tutor_titulo');
  const hayConversacion = mensajes.length > 0 || cargando || !!parcial;

  if (!abierto) return null;

  return (
    <>
      <Velo onClick={onCerrar} />
      <Dock $alaIzquierda={indiceALaDerecha} role="complementary" aria-label={t('tutor_titulo')}>
        <Cabecera>
          <CaraDocente docente={docente} />
          <Identidad>
            <Nombre>{quizActive ? t('tutor_repaso') : nombreTutor}</Nombre>
            {quizActive ? (
              <Pasos aria-label={`${quizPregunta}/3`}>
                {[1, 2, 3].map(n => <Paso key={n} $activo={n <= quizPregunta} />)}
              </Pasos>
            ) : (
              <Papel>{docente?.cargo || t('tutor_subtitulo')}</Papel>
            )}
          </Identidad>
          <CerrarBtn type="button" onClick={onCerrar} title={t('tutor_cerrar')} aria-label={t('tutor_cerrar')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </CerrarBtn>
        </Cabecera>

        <Conversacion>
          {!hayConversacion && !quizActive && (
            <Saludo>
              <strong>{userName ? t('tutor_saludo_con_nombre').replace('{nombre}', userName) : t('tutor_saludo')}</strong>
              {t('tutor_saludo_detalle')}
            </Saludo>
          )}

          {mensajes.map((m, i) => (
            m.role === 'user'
              ? <Pregunta key={i}>{m.text}</Pregunta>
              : <Respuesta key={i}>{m.text}</Respuesta>
          ))}

          {parcial && <Respuesta $atenuado>{parcial}</Respuesta>}
          {cargando && <Respuesta $atenuado>{t('tutor_pensando')}</Respuesta>}
          {error && <Respuesta $error>{error}</Respuesta>}
          {micNoDisponible && <Respuesta $aviso>{t('tutor_sin_microfono')}</Respuesta>}
          <div ref={finRef} />
        </Conversacion>

        {aviso && (
          <Aviso>
            <AvisoAccion type="button" onClick={() => onAviso && onAviso(aviso)}>{aviso}</AvisoAccion>
            <AvisoCerrar type="button" onClick={onDescartarAviso} title={t('tutor_cerrar')} aria-label={t('tutor_cerrar')}>×</AvisoCerrar>
          </Aviso>
        )}

        {!quizActive && !voz.activo && sugerencias.length > 0 && (
          <Sugerencias>
            {sugerencias.map((s, i) => (
              <SugerenciaBtn key={i} type="button" disabled={cargando} onClick={() => onSugerencia && onSugerencia(s)}>
                {s}
              </SugerenciaBtn>
            ))}
          </Sugerencias>
        )}

        {voz.activo && (
          <EstadoVoz>
            <span>{voz.etiqueta}</span>
            {voz.hablando && (
              <button
                type="button"
                onClick={voz.onInterrumpir}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              >
                {t('tutor_interrumpir')}
              </button>
            )}
          </EstadoVoz>
        )}

        <Escribir onSubmit={onSubmit}>
          <Campo
            ref={campoRef}
            value={input}
            onChange={e => onInput(e.target.value)}
            placeholder={quizActive ? t('mentor_chat_respuesta') : t('tutor_placeholder')}
            disabled={cargando || voz.activo}
            aria-label={quizActive ? t('mentor_chat_respuesta') : t('tutor_placeholder')}
          />
          {voz.soportado && !quizActive && (
            <Redondo
              type="button"
              onClick={voz.onToggle}
              disabled={cargando}
              $grabando={voz.activo}
              title={voz.activo ? t('mentor_voz_desconectar') : t('mentor_voz_activar')}
              aria-label={voz.activo ? t('mentor_voz_desconectar') : t('mentor_voz_activar')}
            >
              <IconoMic activo={voz.activo} />
            </Redondo>
          )}
          <Redondo type="submit" $primario disabled={!input.trim() || cargando || voz.activo} title={t('tutor_enviar')} aria-label={t('tutor_enviar')}>
            <IconoEnviar />
          </Redondo>
        </Escribir>
      </Dock>
    </>
  );
}
